// ── 3-2. The model's space, laid out deterministically ─────────────
//
// The model returns a relation graph and no coordinates (spec §2). The client
// decides where the nodes go, and the same graph must always produce the same
// picture — otherwise "read again and it looked different" is unattributable:
// the user cannot tell a change in the reading from a change in the drawing.
//
// So: no Math.random, no Date.now, no iteration over unordered structures.
// Everything starts from the fragment ids, sorted, and every later step is a
// pure function of what came before.
//
// WHAT A POSITION HERE IS NOT. The user's space has a centre that means
// something — distance to it is felt resonance. This space does not, and must
// not be drawn as if it did (spec §3-2: no centre point, no "more felt" axis).
// Only the EDGES are read here. A node's position is a drawing convenience: a
// legible arrangement of the same graph, chosen so the links can be followed.
// The gravity term below exists to keep the picture bounded, not to make the
// middle mean anything.

// Same hash as the arrangement id — a small integer from a string, no more.
function hash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

// Fruchterman-Reingold, with every source of nondeterminism removed: a fixed
// iteration count instead of a convergence test on wall-clock, a fixed cooling
// schedule, and a starting position derived from the node id rather than drawn
// at random. Two runs cannot differ.
const ITERATIONS = 300;
const TEMP_START = 0.11;
// Bounding strength, expressed against the ideal separation rather than as a
// loose constant. A node with no relations feels repulsion and gravity only,
// and settles where k²/r = GRAVITY_K·k²·r — that is, at r = 1/√GRAVITY_K,
// which is 0.5 here: half the box, whatever the fragment count. Tuned as a
// constant instead, unlinked nodes drift far out, and the fit-to-box step then
// shrinks the related ones into an illegible knot.
const GRAVITY_K = 4;
// A strong relation pulls harder, so the picture separates the two fits before
// the line style does. This is the only place fit changes geometry.
const PULL = { strong: 1, weak: 0.4 };

export function readingNodeIds(reading) {
  if (!reading) return [];
  const ids = new Set([
    ...(reading.relations || []).flatMap(r => [r.a, r.b]),
    ...(reading.unlinked || []),
    ...(reading.fragments || []).map(f => f.id),
  ]);
  // Sorted, always. Everything downstream iterates this array, so the layout
  // cannot depend on the order the model happened to list things in.
  return [...ids].filter(Boolean).sort();
}

// Returns positions in a unit box, [0,1] on both axes, plus the edges that
// produced them. Unit box is the STORAGE form — see toPixels for why it is not
// simply multiplied out by width and height.
export function layoutReading(reading) {
  const ids = readingNodeIds(reading);
  const n = ids.length;
  const index = new Map(ids.map((id, i) => [id, i]));

  const edges = (reading?.relations || [])
    .filter(r => index.has(r.a) && index.has(r.b) && r.a !== r.b)
    .map(r => ({ a: r.a, b: r.b, fit: r.fit === "strong" ? "strong" : "weak", label: r.label }))
    // sorted so the accumulation order of the attractive forces is fixed
    .sort((p, q) => (p.a + p.b < q.a + q.b ? -1 : p.a + p.b > q.a + q.b ? 1 : 0));

  if (n === 0) return { nodes: [], edges: [] };
  if (n === 1) return { nodes: [{ id: ids[0], x: 0.5, y: 0.5 }], edges: [] };

  // Starting ring. The angle comes from the id, so a node lands in the same
  // place every time regardless of what else is on the canvas, and two nodes
  // are never placed at exactly the same point (which repulsion could not
  // separate — equal and opposite forces cancel).
  const px = new Float64Array(n), py = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const h = hash(ids[i]);
    const angle = ((h % 100000) / 100000) * Math.PI * 2;
    const radius = 0.28 + (((h >>> 11) % 1000) / 1000) * 0.14;
    px[i] = Math.cos(angle) * radius;
    py[i] = Math.sin(angle) * radius;
  }

  const k = Math.sqrt(1 / n);          // ideal separation
  const dx = new Float64Array(n), dy = new Float64Array(n);

  for (let step = 0; step < ITERATIONS; step++) {
    dx.fill(0); dy.fill(0);

    // repulsion, every pair
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let ex = px[i] - px[j], ey = py[i] - py[j];
        let d = Math.hypot(ex, ey);
        if (d < 1e-6) { ex = 1e-6; ey = 0; d = 1e-6; }   // deterministic nudge, never random
        const f = (k * k) / d;
        const ux = (ex / d) * f, uy = (ey / d) * f;
        dx[i] += ux; dy[i] += uy;
        dx[j] -= ux; dy[j] -= uy;
      }
    }

    // attraction along the relations the model actually drew
    for (const e of edges) {
      const i = index.get(e.a), j = index.get(e.b);
      let ex = px[i] - px[j], ey = py[i] - py[j];
      let d = Math.hypot(ex, ey);
      if (d < 1e-6) continue;
      const f = ((d * d) / k) * PULL[e.fit];
      const ux = (ex / d) * f, uy = (ey / d) * f;
      dx[i] -= ux; dy[i] -= uy;
      dx[j] += ux; dy[j] += uy;
    }

    // Bounding term only. Without it an unlinked node feels repulsion and
    // nothing else, and walks off the picture forever. It is NOT a centre the
    // user is invited to read: nothing is drawn there and no number derives
    // from distance to it.
    const gravity = GRAVITY_K * k * k;
    for (let i = 0; i < n; i++) {
      dx[i] -= px[i] * gravity;
      dy[i] -= py[i] * gravity;
    }

    const temp = TEMP_START * (1 - step / ITERATIONS);
    for (let i = 0; i < n; i++) {
      const d = Math.hypot(dx[i], dy[i]);
      if (d < 1e-12) continue;
      const m = Math.min(d, temp) / d;
      px[i] += dx[i] * m;
      py[i] += dy[i] * m;
    }
  }

  // Fit to the unit box. Uniform on both axes — the SAME lesson as 3-1: scaling
  // x and y by different numbers stretches the picture, and two relations drawn
  // the same length would no longer be the same length. Here that is only a
  // matter of the drawing telling the truth about itself, but it is free to get
  // right, and getting it wrong is how a viewer starts reading distances.
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    if (px[i] < minX) minX = px[i]; if (px[i] > maxX) maxX = px[i];
    if (py[i] < minY) minY = py[i]; if (py[i] > maxY) maxY = py[i];
  }
  const spanX = maxX - minX, spanY = maxY - minY;
  const span = Math.max(spanX, spanY);
  const scale = span > 1e-9 ? 1 / span : 0;
  const offX = (1 - spanX * scale) / 2, offY = (1 - spanY * scale) / 2;

  const nodes = ids.map((id, i) => ({
    id,
    x: span > 1e-9 ? (px[i] - minX) * scale + offX : 0.5,
    y: span > 1e-9 ? (py[i] - minY) * scale + offY : 0.5,
  }));

  return { nodes, edges };
}

// Unit box → pixels, with a margin, scaled UNIFORMLY and centred. A panel is
// rarely square, and multiplying x by the width and y by the height would
// squash the graph differently on every screen — the same distortion 3-1 keeps
// out of the measurement, kept here out of the picture.
export function toPixels(layout, { w, h, margin = 26 }) {
  const boxW = Math.max(0, w - margin * 2), boxH = Math.max(0, h - margin * 2);
  const side = Math.min(boxW, boxH);
  const offX = margin + (boxW - side) / 2, offY = margin + (boxH - side) / 2;
  const at = new Map(layout.nodes.map(nd => [nd.id, { x: offX + nd.x * side, y: offY + nd.y * side }]));
  return {
    nodes: layout.nodes.map(nd => ({ ...nd, ...at.get(nd.id) })),
    edges: layout.edges.map(e => ({ ...e, from: at.get(e.a), to: at.get(e.b) })),
  };
}
